import React, { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Phone, Mail, MapPin, Clock, CheckCircle, 
  MessageSquare, Calendar, Loader2
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useToast } from "../hooks/use-toast";
import axios from 'axios';

const therapistInfo = {
  name: "Keith Scherdt, LPC, LMHC",
  phone: "(808) 470-7650",
  email: "keith@mhcmaui.com",
  address: "2200 Main Street",
  city: "Wailuku",
  state: "HI",
  zip: "96793",
  location: "Wailuku, HI"
};

const insuranceProviders = [
  { name: "Blue Cross Blue Shield" },
  { name: "Aetna" },
  { name: "United Healthcare" },
  { name: "Cigna" },
  { name: "Humana" },
  { name: "Medicare" }
];

const faqs = [
  {
    id: 1,
    question: "How do I schedule an appointment?",
    answer: "You can schedule an appointment by calling us at (808) 470-7650, emailing us at keith@mhcmaui.com, or using the contact form on this page. We typically respond within 24 hours."
  },
  {
    id: 2,
    question: "Do you accept insurance?",
    answer: "Yes, we accept most major insurance providers including Blue Cross Blue Shield, Aetna, United Healthcare, Cigna, Humana, and Medicare. We recommend contacting your insurance provider to verify your mental health benefits before your first appointment."
  },
  {
    id: 3,
    question: "What should I expect during my first session?",
    answer: "Your first session is an opportunity for us to get to know each other. We'll discuss what brings you to therapy, your goals, and answer any questions you have. This helps us develop a personalized treatment plan together."
  },
  {
    id: 4,
    question: "Are telehealth appointments available?",
    answer: "Yes, we offer secure telehealth appointments for your convenience. This is a great option if you prefer to meet from the comfort of your home or have scheduling constraints."
  },
  {
    id: 5,
    question: "How long are therapy sessions?",
    answer: "Standard therapy sessions are typically 50-60 minutes. Family therapy sessions may be longer depending on your specific needs."
  }
];

export const ContactPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/contact/inquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        subject: formData.subject,
        message: formData.message
      });

      if (response.data.success) {
        toast({
          title: "Message Sent Successfully!",
          description: response.data.message || "We'll get back to you within 24 hours.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-teal-100 text-teal-800">Get In Touch</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Start Your Journey Today
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to take the first step? Reach out to schedule a consultation 
            or ask any questions you may have.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-none shadow-lg bg-white text-center hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-teal-600" />
                </div>
                <CardTitle className="text-xl">Call Us</CardTitle>
              </CardHeader>
              <CardContent>
                <a 
                  href={`tel:${therapistInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-lg transition-colors"
                >
                  {therapistInfo.phone}
                </a>
                <p className="text-sm text-gray-600 mt-2">Mon-Fri: 9am - 6pm</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white text-center hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Email Us</CardTitle>
              </CardHeader>
              <CardContent>
                <a 
                  href={`mailto:${therapistInfo.email}`}
                  className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                >
                  {therapistInfo.email}
                </a>
                <p className="text-sm text-gray-600 mt-2">We'll respond within 24 hours</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white text-center hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Visit Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 font-medium">{therapistInfo.address}</p>
                <p className="text-gray-700 font-medium">{therapistInfo.city}, {therapistInfo.state} {therapistInfo.zip}</p>
                <p className="text-sm text-gray-600 mt-2">Telehealth also available</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Send Us a Message
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 border-2 border-gray-300 focus:border-teal-500 transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 border-2 border-gray-300 focus:border-teal-500 transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 border-2 border-gray-300 focus:border-teal-500 transition-colors"
                      placeholder="(808) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="mt-2 border-2 border-gray-300 focus:border-teal-500 transition-colors"
                    placeholder="Appointment Request"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="mt-2 border-2 border-gray-300 focus:border-teal-500 transition-colors resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Additional Info */}
            <div className="space-y-8">
              <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-blue-50">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <Calendar className="w-6 h-6 text-teal-600" />
                    <CardTitle className="text-2xl">Schedule an Appointment</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    Ready to get started? Call us directly or send a message using the form 
                    to schedule your initial consultation.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                      <p className="text-gray-700">Initial consultations available</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                      <p className="text-gray-700">Flexible scheduling options</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                      <p className="text-gray-700">Telehealth appointments available</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                      <p className="text-gray-700">Currently accepting new clients</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200"
                    onClick={() => window.location.href = `tel:${therapistInfo.phone.replace(/[^0-9]/g, '')}`}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Office Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-gray-700 font-medium">Monday - Friday</span>
                    <span className="text-gray-600">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-gray-700 font-medium">Saturday</span>
                    <span className="text-gray-600">By Appointment</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-700 font-medium">Sunday</span>
                    <span className="text-gray-600">Closed</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Insurance Accepted</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {insuranceProviders.map((provider, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{provider.name}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-4">
                    Please contact your insurance provider to verify mental health benefits.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">FAQ</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Find answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-white border-none shadow-md rounded-lg px-6"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};
